import { useCallback, useEffect, useState } from 'react';

type PlainObjectOrPlainObjectArray = Record<string, unknown> | Record<string, unknown>[];

class JsonField<T extends PlainObjectOrPlainObjectArray> {
  private readonly value: T;

  public constructor(initialValue: T) {
    this.value = initialValue;
  }

  public getJSON(): T {
    return this.value;
  }

  public tryStringify(): string {
    try {
      return JSON.stringify(this.value);
    } catch (error) {
      // Log the error to aid debugging when JSON serialization fails
      console.error('Failed to stringify JsonField value:', error);
      return '';
    }
  }
}

interface UseJsonFieldReturn<T extends PlainObjectOrPlainObjectArray> {
  stringValue: string;
  error: string;
  handleChange: (value: string) => void;
  parseValue: () => JsonField<T> | undefined;
  clearError: () => void;
}

/**
 * Custom hook to manage JSON fields with validation
 * @param initialValue - Initial JSON value to be stringified
 * @returns Object containing string value, error state, and handlers
 */
export const useJsonField = <T extends PlainObjectOrPlainObjectArray>(
  initialValue?: T | null
): UseJsonFieldReturn<T> => {
  const [stringValue, setStringValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setStringValue(initialValue ? JSON.stringify(initialValue, null, 2) : '');
  }, [initialValue]);

  const handleChange = useCallback((value: string) => {
    setStringValue(value);
    setError('');
  }, []);

  const parseValue = useCallback((): JsonField<T> | undefined => {
    if (!stringValue) {
      return undefined;
    }

    try {
      const parsedValue = JSON.parse(stringValue) as T;
      return new JsonField<T>(parsedValue);
    } catch (err: unknown) {
      setError((err as Error).message);
      throw err;
    }
  }, [stringValue]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    stringValue,
    error,
    handleChange,
    parseValue,
    clearError
  };
};
